import { useAuth } from '@berky/auth'
import { useEffect } from 'react'
import { useLocation } from 'react-router'

import { moduleAccess } from '../module-access'
import { modules } from '../modules'
import { usePlatformUsageApi } from './api'
import { berlinDate } from './usage-utils'

const STORAGE_KEY = 'berky:usage:last-module'
const pageLoadId = newPageLoadId()

interface TrackedLocation {
  pageLoadId: string
  moduleId: string | null
  userId?: string
  date?: string
}

/**
 * Counts a module entry but not navigation within the same module.
 *
 * The sessionStorage state prevents React StrictMode's duplicate effect from
 * counting twice. A browser reload gets a new pageLoadId and therefore counts
 * a direct re-entry again.
 */
export function ModuleUsageTracker() {
  const location = useLocation()
  const { user } = useAuth()
  const userId = user?.id
  const api = usePlatformUsageApi()
  const firstSegment = location.pathname.split('/').filter(Boolean)[0]
  const currentModule = modules.find(
    (module) =>
      module.id === firstSegment && moduleAccess(user?.roles, module) === 'erlaubt',
  )
  const moduleId = currentModule?.id ?? (location.pathname === '/' ? 'platform' : null)

  useEffect(() => {
    if (!userId) return
    const previous = readTrackedLocation()
    const date = berlinDate()
    if (
      previous?.pageLoadId === pageLoadId &&
      previous.moduleId === moduleId &&
      previous.userId === userId &&
      previous.date === date
    )
      return

    writeTrackedLocation({ pageLoadId, moduleId, userId, date })
    if (!moduleId) return

    // Analytics must never block a module or surface an error in its workspace.
    void api.post<void>(`/modules/${moduleId}`).catch(() => undefined)
  }, [api, moduleId, userId, location.pathname])

  return null
}

function readTrackedLocation(): TrackedLocation | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as TrackedLocation) : null
  } catch {
    return null
  }
}

function writeTrackedLocation(location: TrackedLocation) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(location))
  } catch {
    // Browsers without session storage must remain usable. StrictMode can
    // locally count twice in this rare fallback case.
  }
}

function newPageLoadId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random()}`
}
